import { useCallback, useMemo, useState } from "react";

import qs from "qs";
import { useNavigate, Outlet, useMatch, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AnimatePresence, motion } from "framer-motion";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import {
  useClientPlan,
  useCommunicationProvider,
  useDeliveryReceiver,
  useDigitalReceptionMetadata,
  useTeamsVideoCallEnabled,
} from "../../lib/hooks/useQueryBoard";
import { useLanguage } from "../../bit/language";

import Bump from "../../components/Animations/Bump";
import Title from "../../components/Title";
import Curve from "../../components/Animations/PageTransitions/Curve";
import Modal from "../../components/Modal";
import Reveal from "../../components/Animations/Reveal";
import BackButton from "../../components/BackButton";
import OfflineModal from "../../components/Offline/OfflineModal";

import { client } from "../../bit/board/hooks/useGraphQLClient";

import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import { useTeamsCallerTokenAndTargetTeamsUserIds } from "../../lib/hooks/useTeamsCallerTokenAndTargetTeamsUserIds";
import { useDeliverySuccessMessage } from "../../lib/hooks/useDeliverySuccessMessage";

import { MUTATION_CREATE_CUSTOM_DELIVERY_EVENT } from "../../config/graphql/mutation";
import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";

const CustomDeliveryPage = () => {
  const match = useMatch({ path: "/custom-delivery/:action" });

  const action = match?.params?.action as "package" | "food";

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/custom-delivery\/${action}\/?$`).test(
    pathname,
  );

  const namespace = action === "food" ? "foodDelivery" : "packageDelivery";

  const [calling, setCalling] = useState<boolean>(false);

  const clientPlan = useClientPlan();

  const queryClient = useQueryClient();

  const { t } = useTranslation([namespace]);

  const navigate = useNavigate();

  const communicationProvider = useCommunicationProvider();
  const deliveryReceiver = useDeliveryReceiver();
  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();

  const metadata = useDigitalReceptionMetadata();
  const [language] = useLanguage();

  const titleOverride =
    metadata?.uiOverrides?.customDelivery?.flows?.[action]?.title?.[language];
  const subtitle1Override =
    metadata?.uiOverrides?.customDelivery?.flows?.[action]?.subtitle1?.[
      language
    ];
  const subtitle2Override =
    metadata?.uiOverrides?.customDelivery?.flows?.[action]?.subtitle2?.[
      language
    ];
  const submitButtonOverride =
    metadata?.uiOverrides?.customDelivery?.flows?.[action]?.buttons?.submit?.[
      language
    ];

  const {
    data,
    status: requestStatus,
    mutate: onCreateCustomDeliveryEvent,
  } = useMutation(
    async (input: {
      type: string;
      receiver: { fullName: string; email: string; phone?: string };
    }) => {
      const response = await client.request<
        { createCustomDeliveryEvent: IAccessEvent },
        {
          input: {
            type: string;
            receiver: { fullName: string; email: string; phone?: string };
          };
        }
      >(MUTATION_CREATE_CUSTOM_DELIVERY_EVENT, {
        input,
      });

      const accessScanId = response?.createCustomDeliveryEvent?.id;

      /**
       * Prefetch access event so we don't wait when on contact page
       */
      await queryClient
        .prefetchQuery([`access/event/${accessScanId}`], () =>
          client.request(QUERY_ACCESS_EVENT, { id: accessScanId }),
        )
        .catch(() => null);

      return response;
    },
  );

  const status = useDebounceStatus(requestStatus, 1500);

  const translationKey = useMemo(() => {
    const key = `${namespace}:status.${status}`;

    if (status !== "success") {
      return key;
    }

    let messageType = "no-email-sms";

    if (
      (!communicationProvider || communicationProvider === "twilio") &&
      !!deliveryReceiver?.phone
    ) {
      messageType = "call-ping";
    } else {
      messageType = "email-sms";
    }

    return `${key}.${messageType}`;
  }, [namespace, status, deliveryReceiver?.phone, communicationProvider]);

  const shouldFetchTeamsToken = useMemo(() => {
    return communicationProvider === "teams" && !!deliveryReceiver?.email;
  }, [communicationProvider, deliveryReceiver?.email]);

  const { callerAcsToken, targetTeamsUserIds, isGroupCall } =
    useTeamsCallerTokenAndTargetTeamsUserIds({
      emails: deliveryReceiver?.email ? [deliveryReceiver.email] : [],
      enabled: shouldFetchTeamsToken,
    });

  const onDismiss = useCallback(() => {
    const id = data?.createCustomDeliveryEvent?.id;

    const isFree = clientPlan && clientPlan === "free";

    const canNavigate = !isFree && !!id && status === "success";

    if (
      canNavigate &&
      communicationProvider !== "teams" &&
      !!deliveryReceiver?.phone
    ) {
      setCalling(true);

      return navigate(`/custom-delivery/${action}/contact/${id}/call`, {
        replace: true,
      });
    }

    if (
      canNavigate &&
      communicationProvider === "teams" &&
      !!deliveryReceiver?.email
    ) {
      return navigate(
        `/${
          teamsVideoCallEnabled
            ? "teams-video"
            : `custom-delivery/${action}/teams-audio`
        }-${isGroupCall ? "group-" : ""}call?${qs.stringify({
          callerAcsToken,
          targetTeamsUserIds,
        })}`,
        {
          replace: true,
        },
      );
    }
    return navigate("/", { replace: true });
  }, [
    status,
    data,
    callerAcsToken,
    targetTeamsUserIds,
    communicationProvider,
    deliveryReceiver,
  ]);

  const successMessage = useDeliverySuccessMessage(action);

  const overriddenTitle =
    status === "success" ? successMessage.title : undefined;

  const overriddenMessage =
    status === "success" ? successMessage.message : undefined;

  const timeout = useMemo(() => {
    if (status === "error") {
      return 6000;
    }

    if (status === "success") {
      return successMessage.durationMs || 3000;
    }

    return 0;
  }, [status, successMessage.durationMs]);

  useTimeoutFunction(onDismiss, timeout);

  const onSubmit = useCallback(() => {
    if (!(deliveryReceiver?.email || deliveryReceiver?.fullName)) {
      return;
    }

    const input = {
      type: action === "package" ? "Package" : "Food",
      receiver: {
        fullName: deliveryReceiver.fullName,
        email: deliveryReceiver.email,
        ...((!communicationProvider || communicationProvider === "twilio") &&
          deliveryReceiver.phone && { phone: deliveryReceiver.phone }),
      },
    };

    onCreateCustomDeliveryEvent(input);
  }, [
    onCreateCustomDeliveryEvent,
    deliveryReceiver,
    action,
    communicationProvider,
  ]);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1" className="delivery-title">
            {titleOverride ?? <Trans i18nKey={`${namespace}:title`} />}
          </Typography>
        </Title>
        <Reveal className="text-center">
          <Typography variant="h3" className="font-weight-regular mt-5">
            {subtitle1Override ?? <Trans i18nKey={`${namespace}:subtitle1`} />}
          </Typography>
          <Typography variant="h4" className="font-weight-regular mt-4 mb-5">
            {subtitle2Override ?? <Trans i18nKey={`${namespace}:subtitle2`} />}
          </Typography>
        </Reveal>
        <Bump disabled={!mainRoute}>
          <motion.div style={{ width: "54rem" }}>
            <Button
              variant="contained"
              className="mx-1 mt-5 w-100"
              onClick={onSubmit}
            >
              {submitButtonOverride ?? t(`${namespace}:button.submit`)}
            </Button>
          </motion.div>
        </Bump>

        <AnimatePresence mode="wait">
          {status !== "idle" && !calling && (
            <Modal
              status={status}
              title={
                overriddenTitle ?? (
                  // @ts-ignore
                  <Trans i18nKey={`${translationKey}.title`} />
                )
              }
              message={
                overriddenMessage ?? (
                  // @ts-ignore
                  <Trans i18nKey={`${translationKey}.message`} />
                )
              }
              onDismiss={onDismiss}
            />
          )}
        </AnimatePresence>

        <Outlet />
        <OfflineModal />
      </div>
    </Curve>
  );
};

export default CustomDeliveryPage;
